<template>
  <b-modal id="task-show-modal"
           :title="task ? task.title : $t('task')"
           size="lg"
           @hidden="onHide"
           @show="onShow"
           centered>
    <div v-if="task">
      <div v-if="!task.onlyComments">
        <div class="row justify-content-between">
          <div class="mb-3 col col-auto">
            <label class="form-label">{{ $t('type') }}</label>
            <div>{{ $t(task.task_type) }}</div>
          </div>
          <div class="mb-3 col col-auto">
            <div>
              <label class="form-label">{{ $t('importance') }}</label>
              <div>
                <CRMDatatableTDTaskType :object="task"
                                        fieldType="importance"/>
              </div>
            </div>
          </div>
          <div class="mb-3 col col-auto" v-if="task.start">
            <div>
              <label class="form-label">{{ $t('start_period') }}</label>
              <div>{{ formatDateTime(task.start) }}</div>
            </div>
          </div>
          <div class="mb-3 col col-auto">
            <div>
              <label class="form-label">{{ $t('deadline') }}</label>
              <div>{{ task.deadline ? formatDateTime(task.deadline) : '-' }}</div>
            </div>
          </div>
          <div class="mb-3 col-12" v-if="task.patient">
            <div>
              <label class="form-label">{{ $t('patient') }}</label>
              <div class="d-flex align-items-center">
                <!--                <a @click="toPatientPage" class="text-primary pointer">{{ task.patient.full_name }}</a>-->
                <router-link :to="{ name: 'PatientVisits', params: { id: task.patient_id }}"
                             @click="$bvModal.hide('task-show-modal')"
                             class="text-primary pointer">
                  {{ task.patient.full_name }}
                </router-link>
                <span v-if="phone && !rights.includes('hide_patient_phone')"
                      class="text-primary">
                  ({{ phone }})
                </span>
                <a class="ml-3"
                   v-if="!rights.includes('hide_patient_phone')"
                   :href="`tel:${phone}`"
                   v-b-tooltip.hover
                   :title="$t('call')">
                  <PhoneSvg class="svg-primary"/>
                </a>
                <a v-if="clinic && clinic.sms_info && rights.includes('sms_send') && !rights.includes('hide_patient_phone')"
                   v-b-tooltip.hover
                   :title="$t('send_sms')"
                   class="ml-3 pointer"
                   @click="smsModal(phone)">
                  <SmsSvg class="svg-primary"/>
                </a>

                <WhatsappTelegramIcon v-if="patient && !rights.includes('hide_patient_phone')"
                                      :top-position="0"
                                      :patient-id="patient.id"
                                      :phone="patient.phone"/>

                <b-dropdown variant="link" v-if="rights.includes('crm')" class="ml-3">
                  <template #button-content>
                    <a v-b-tooltip.hover
                       class="pointer"
                       :title="$t('add_to_funnel')">
                      <FilterSvg class="svg-primary svg-size-lg"/>
                    </a>
                  </template>
                  <b-dropdown-item @click="addToLeads">
                    {{ $t('applications_and_appeals') }}
                  </b-dropdown-item>
                  <b-dropdown-item @click="addToPatientCanban">
                    {{ $t('funnel_came_and_repeated') }}
                  </b-dropdown-item>
                </b-dropdown>


              </div>
            </div>
          </div>
        </div>
        <div v-if="task.lead" class="mb-3">
          <a class="pointer"
             @click="leadClicked">
            {{ $t('lead') }}#{{ task.lead_id }}
          </a>
        </div>
        <hr class="mt-0"/>
        <div class="row">
          <div class="col">
            <label class="form-label">{{ $t('status') }}</label>
            <div>
              <SelectGroup :options="taskStatuses"
                           v-model="task.status"
                           @update:modelValue="changeStatus"
                           variant="white"
                           class="md"
                           :showlabel="false"
                           :label="$t('status')"/>
            </div>
          </div>
          <div class="col" v-if="task.user">
            <label class="form-label">{{ $t('responsible') }}</label>
            <div>{{ task.user.full_name }}</div>
          </div>
          <div class="col" v-if="task.author">
            <label class="form-label">{{ $t('author') }}</label>
            <div>{{ task.author.full_name }}</div>
          </div>
        </div>
        <div class="mt-3" v-if="task.description">
          <label class="form-label black">{{ $t('description') }}</label>
          <div>{{ task.description }}</div>
        </div>
        <div class="mt-3" v-if="task.files && task.files.length">
          <div v-for="(t, tind) in task.files" :key="`file-${tind}`">
            <a v-if="t.original" :href="t.fileUrl" target="_blank">
              {{ t.original }}
            </a>
          </div>
        </div>
      </div>
      <GeneralCommentsBlock :object="task"
                            class="mt-4"
                            model="task_comment"
                            parent-column="task_id"/>
    </div>
    <template #modal-footer="{ close }">
      <div class="modal-footer-buttons no-print">
        <b-button class="cancel mr-md-3 mr-2 px-3 d-inline-block align-middle w-auto"
                  variant="themed"
                  @click="edit">
          {{ $t('edit') }}
        </b-button>
        <button :disabled="loading"
                @click="close()"
                class="btn-themed align-middle btn-themed-squared">
          {{ $t('done') }}
        </button>
      </div>
    </template>
  </b-modal>
</template>

<script setup>
import CRMDatatableTDTaskType from "@/components/parts/datatable/td/crm/CRMDatatableTDTaskType"
import GeneralCommentsBlock from "@/components/parts/general/GeneralCommentsBlock"
import { formatDateTime, formatPhone } from "@/extensions/filters/filters"
import { EloquentService } from "@/services/api/eloquent.api.service"
import { CRMService } from "@/services/api/crm.api.service"
import {CalendarService} from "@/services/api/calendar.api.service"
import SelectGroup from "@/components/form/SelectGroup.vue"
import { taskStatuses } from "@/dictionaries/dictionary"
import PhoneSvg from "@/assets/svg-vue/header/phone_t.svg.vue"
import WhatsappTelegramIcon from "@/components/pages/patients/WhatsappTelegramIcon.vue"
import SmsSvg from "@/assets/svg-vue/general/sms_t.svg.vue"
import FilterSvg from "@/assets/svg-vue/sidebar/crm.svg.vue"
import { copyObject } from "@/extensions/prototypes/prototypes"
import i18n from "@/lang/i18n"
import { computed, getCurrentInstance, nextTick } from "vue"
import { useStore } from "vuex"
import { useRoute, useRouter } from "vue-router"

const store = useStore()
const route = useRoute()
const router = useRouter()
const instance = getCurrentInstance()
const noty = instance?.appContext?.app?.noty
const bvModal = instance?.proxy?.$bvModal

const clinic = computed(() => store.state.auth.clinic)
const loading = computed(() => store.state.dom.loading)
const task = computed(() => store.state.crm.task) // the shown task (crm.task)
const rights = computed(() => store.state.auth.rights)

const phone = computed(() => formatPhone(task.value.phone || task.value.patient?.phone))
const patient = computed(() => task.value.patient)

// edit in the datatable form modal
const edit = () => {
  bvModal.hide('task-show-modal')
  let newObject = JSON.parse(JSON.stringify(task.value))
  store.commit('setModule', 'crm')
  store.commit('setSelectedModel', newObject)
  store.commit('setViewModel', 'task')
  nextTick(() => {
    store.commit('setShowModal', true)
  })
}

const changeStatus = async (status) => {
  let res = await CalendarService.finishTask(task.value.id, status)
  if(res.data) store.commit('incrementDataTableCounter')
}

const smsModal = async () => {
  store.commit('setPatient', patient.value)
  bvModal.show('patient-sms-modal')
}

// the task can be opened by a link with ?id= - removed when closed
const onHide = () => {
  const queryParams = { ...route.query }
  if(queryParams.id) {
    delete queryParams.id
    router.replace({ query: queryParams })
  }
}

// the full task (comments, files...)
const onShow = async () => {
  const res = await EloquentService.show('task', task.value.id)
  store.commit('setTask', copyObject(res.data))
}

const leadClicked = async () => {
  if(!task.value.lead_id) return
  bvModal.hide('task-show-modal')
  const leadFull = await EloquentService.show('lead', task.value.lead_id)
  store.commit('setLead', copyObject(leadFull.data))
  bvModal.show('lead-show-modal')
}

const createCanbanFromTask = async (type) => {
  try {
    const res = await CRMService.createCanbanFromTask({
      task_id: task.value.id,
      type
    })
    if(res?.data) {
      noty.info(i18n.global.t('success_message'))
    }
  } catch (e) {
    console.log(e)
  }
}

const addToLeads = async () => {
  await createCanbanFromTask('lead')
}

const addToPatientCanban = async () => {
  await createCanbanFromTask('patient_canban')
}
</script>
